// SKILLSMITH-HUB (Andrei, 2026-10-30): an agent files an entry in its company's "Agent issues"
// log (the MCP `report_issue` tool) when it hits a problem — a tool error, a blocker, a confusing
// instruction, a repeated failure, a person correcting it. The log is write-only for the company:
// the answer is `{accepted: true}` and nothing else, no route lists or reads entries back, and
// only the company's system agent (operator side) reads them. The platform files its own entries
// for hard failures (a failed or timed-out run, a parked retry, a BLOCKED verdict loop).
import { z } from "zod";
import { SystemAgentFeedbackSeverity } from "../enums";
import { Uuid } from "../primitives";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents" } as const;

/** The kinds an agent files (the platform's hard-failure kinds are its own). */
export const AgentIssueReportKind = z
  .enum(["tool_error", "blocker", "confusing_instruction", "repeated_failure", "human_correction"])
  .meta({ id: "AgentIssueReportKind" });

export const AgentIssueCreate = z
  .strictObject({
    kind: AgentIssueReportKind,
    /** The agent's guess; a `critical` entry wakes the company's system agent. */
    severity: SystemAgentFeedbackSeverity,
    /** What happened, in a sentence or two. */
    what: z.string().trim().min(1).max(500),
    /** One line of context (the tool, the step, the instruction). */
    context: z
      .string()
      .trim()
      .min(1)
      .max(300)
      .regex(/^[^\r\n]*$/, "one line")
      .optional(),
    run_id: Uuid.optional(),
    task_id: Uuid.optional(),
    /** An AG filing through its DE names itself here. */
    reporter_label: z.string().trim().min(1).max(120).optional(),
    /** The agent's own key for this entry: a retry with it files once. */
    request_key: z.string().min(1).max(128).optional(),
  })
  .meta({ id: "AgentIssueCreate" });
export type AgentIssueCreate = z.infer<typeof AgentIssueCreate>;

export const AgentIssueAccepted = z
  .strictObject({ accepted: z.literal(true) })
  .meta({ id: "AgentIssueAccepted" });

export const agentIssueRoutes = {
  "agentIssues.create": defineRoute({
    ...agents,
    id: "agentIssues.create",
    method: "post",
    path: `${base}/agent/issues`,
    tag: "runs",
    summary: "report_issue: an agent files an entry in its company's Agent issues log",
    description:
      "The DE's agent key (or its run); people are refused. Write-only: the " +
      "answer is `{accepted: true}`, never the entry, and no route reads entries back to the " +
      "company. A repeated `request_key` files once. A `critical` entry wakes the company's " +
      "system agent after commit (best effort).",
    access: "agent_key",
    params: CompanyParams,
    body: AgentIssueCreate,
    // No company event: the entry goes to the operator-only log, and an event in the company's
    // log would show the company its system agent. A read for the event rules (as
    // `operator.systemAgents.feedback.create`).
    safe: true,
    response: { status: 202, schema: AgentIssueAccepted },
    errors: [],
    emits: [],
  }),
} as const;
